/* ============================================================
 * Lion Theme — lion-cart-drawer.js
 * Custom elements: <lion-cart-drawer>, <lion-cart-drawer-items>, <lion-quantity-input>.
 * Subscribes to PUB_SUB cart-update events and refreshes via
 * Section Rendering API (?sections=cart-drawer).
 * ============================================================ */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.addEventListener("keyup", (e) => {
      if (e.code?.toUpperCase() === "ESCAPE") this.close();
    });
    this.querySelectorAll("[data-cart-drawer-close]").forEach((el) =>
      el.addEventListener("click", () => this.close()),
    );
    this.setHeaderCartIconAccessibility();
    this.addEventListener("submit", this.onSubmit.bind(this));
    this.cartUpdateUnsub = window.subscribe?.(
      window.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this),
    );
    this.initDiscountHandler();
    this.initShippingEstimate();
  }

  initDiscountHandler() {
    this.addEventListener("click", async (e) => {
      const applyBtn = e.target.closest("[data-cart-discount-apply]");
      if (applyBtn) {
        e.preventDefault();
        const input = this.querySelector("#CartDrawerDiscount");
        const errorEl = this.querySelector("[data-cart-discount-error]");
        if (!input || !input.value.trim()) return;
        this.setLoading(applyBtn, true);
        try {
          const formData = new FormData();
          formData.append("discount", input.value.trim());
          formData.append("sections", "cart-drawer");
          formData.append("sections_url", window.location.pathname);
          const res = await fetch(`${window.shopUrls.cart_update}`, {
            method: "POST",
            body: formData,
          });
          const state = await res.json();
          if (state.sections?.["cart-drawer"]) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(
              state.sections["cart-drawer"],
              "text/html",
            );
            const fresh = doc.querySelector("lion-cart-drawer");
            if (fresh) this.innerHTML = fresh.innerHTML;
            this.querySelectorAll("[data-cart-drawer-close]").forEach((el) =>
              el.addEventListener("click", () => this.close()),
            );
            this.updateHeaderBadge();
            this.initShippingEstimate();
          }
          if (
            state.discount_applications?.length === 0 &&
            state.discount_codes?.some((d) => !d.applicable)
          ) {
            errorEl.textContent =
              window.themeStrings?.discountApplyError ||
              "Discount code not applicable";
            errorEl.hidden = false;
          } else {
            errorEl.hidden = true;
          }
        } catch (err) {
          errorEl.textContent =
            window.themeStrings?.discountApplyError ||
            "Error applying discount";
          errorEl.hidden = false;
        } finally {
          this.setLoading(applyBtn, false);
        }
      }
      const removeBtn = e.target.closest("[data-cart-discount-remove]");
      if (removeBtn) {
        e.preventDefault();
        this.setLoading(removeBtn, true);
        try {
          const formData = new FormData();
          formData.append("discount", "");
          formData.append("sections", "cart-drawer");
          formData.append("sections_url", window.location.pathname);
          const res = await fetch(`${window.shopUrls.cart_update}`, {
            method: "POST",
            body: formData,
          });
          const state = await res.json();
          if (state.sections?.["cart-drawer"]) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(
              state.sections["cart-drawer"],
              "text/html",
            );
            const fresh = doc.querySelector("lion-cart-drawer");
            if (fresh) this.innerHTML = fresh.innerHTML;
            this.querySelectorAll("[data-cart-drawer-close]").forEach((el) =>
              el.addEventListener("click", () => this.close()),
            );
            this.updateHeaderBadge();
            this.initShippingEstimate();
          }
        } catch (err) {
        } finally {
          this.setLoading(removeBtn, false);
        }
      }
    });
  }

  setLoading(btn, isLoading) {
    btn.disabled = isLoading;
    btn.classList.toggle("is-loading", isLoading);
  }

  initShippingEstimate() {
    const countrySelect = this.querySelector("#CartDrawerCountry");
    const provinceSelect = this.querySelector("#CartDrawerProvince");
    if (!countrySelect || !provinceSelect) return;
    if (window.Shopify && window.Shopify.CountryProvinceSelector) {
      new window.Shopify.CountryProvinceSelector(
        "CartDrawerCountry",
        "CartDrawerProvince",
        { hideElement: "CartDrawerProvince" },
      );
    }
    const calcBtn = this.querySelector("[data-cart-shipping-calc]");
    const resultsEl = this.querySelector("[data-cart-shipping-results]");
    if (!calcBtn || !resultsEl) return;
    calcBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      this.setLoading(calcBtn, true);
      resultsEl.innerHTML = "";
      try {
        const zip = this.querySelector("#CartDrawerZip")?.value?.trim() || "";
        const country = countrySelect.value;
        const province = provinceSelect.value;
        const url = `${window.shopUrls.root}cart/shipping_rates.json?shipping_address[zip]=${encodeURIComponent(zip)}&shipping_address[country]=${encodeURIComponent(country)}&shipping_address[province]=${encodeURIComponent(province)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.shipping_rates && data.shipping_rates.length) {
          const showDays = this.dataset.showDeliveryDays === "true";
          data.shipping_rates.forEach((rate) => {
            const row = document.createElement("div");
            row.className = "shipping-rate";
            let days = "";
            if (showDays && rate.delivery_days && rate.delivery_days.length) {
              const first = rate.delivery_days[0];
              const last = rate.delivery_days[rate.delivery_days.length - 1];
              const dayLabel =
                first === 1
                  ? window.themeStrings?.deliveryDayOne || "Day"
                  : window.themeStrings?.deliveryDayOther || "Days";
              days =
                first === last
                  ? `(${first} ${dayLabel})`
                  : `(${first} - ${last} ${dayLabel})`;
            }
            // Build with textContent to avoid any XSS risk if a third-party
            // shipping provider returns HTML-tainted strings in rate.name.
            const nameEl = document.createElement("span");
            nameEl.textContent = rate.name;
            const priceEl = document.createElement("span");
            priceEl.textContent = `${rate.price} ${window.Shopify?.currency?.active || ""} ${days}`;
            row.append(nameEl, priceEl);
            resultsEl.appendChild(row);
          });
        } else {
          const p = document.createElement("p");
          p.textContent =
            window.themeStrings?.noShippingRate ||
            "No shipping rates available for this destination.";
          resultsEl.replaceChildren(p);
        }
      } catch (err) {
        const p = document.createElement("p");
        p.className = "form__error";
        p.textContent =
          window.themeStrings?.shippingCalcError ||
          "Error calculating shipping rates.";
        resultsEl.replaceChildren(p);
      } finally {
        this.setLoading(calcBtn, false);
      }
    });
  }

  onSubmit(e) {
    const terms = this.querySelector("[data-cart-terms-checkbox]");
    if (terms && !terms.checked) {
      e.preventDefault();
      const error = this.querySelector("[data-cart-terms-error]");
      if (error) error.hidden = false;
      terms.focus();
      return;
    }
    const termsError = this.querySelector("[data-cart-terms-error]");
    if (termsError) termsError.hidden = true;
  }

  disconnectedCallback() {
    this.cartUpdateUnsub?.();
    if (this.onDocumentCartIconClick) {
      document.removeEventListener("click", this.onDocumentCartIconClick);
    }
    if (this.onDocumentCartIconKey) {
      document.removeEventListener("keydown", this.onDocumentCartIconKey);
    }
  }

  setHeaderCartIconAccessibility() {
    // Document-level delegation — survives section hot-reloads, Section
    // Rendering API replacements, and any cart icon rendered after the
    // CartDrawer was constructed. A direct listener on the link broke when
    // the header was re-rendered (the new link had no handler, so it just
    // navigated to /cart instead of opening the drawer).
    this.onDocumentCartIconClick = (e) => {
      const opener = e.target.closest("[data-cart-icon-bubble]");
      if (!opener) return;
      e.preventDefault();
      this.open(opener);
    };
    this.onDocumentCartIconKey = (e) => {
      if (e.code !== "Space" && e.code !== "Enter") return;
      const opener = e.target.closest("[data-cart-icon-bubble]");
      if (!opener) return;
      e.preventDefault();
      this.open(opener);
    };
    document.addEventListener("click", this.onDocumentCartIconClick);
    document.addEventListener("keydown", this.onDocumentCartIconKey);
    // ARIA: present the cart link as a button that opens a dialog.
    document.querySelectorAll("[data-cart-icon-bubble]").forEach((link) => {
      link.setAttribute("role", "button");
      link.setAttribute("aria-haspopup", "dialog");
    });
  }

  open(triggerEl) {
    this.activeElement = triggerEl || document.activeElement;
    this.removeAttribute("hidden");
    // Next frame to ensure transition plays
    requestAnimationFrame(() => {
      this.setAttribute("open", "");
      document.body.classList.add("overflow-hidden");
      window.trapFocus?.(this, this.querySelector("[data-cart-drawer-close]"));
    });
  }

  close() {
    this.removeAttribute("open");
    document.body.classList.remove("overflow-hidden");
    window.removeTrapFocus?.(this.activeElement);
    setTimeout(() => this.setAttribute("hidden", ""), 320);
  }

  async onCartUpdate(data) {
    // data: { source, productVariantId, cartData, errorMessage? }
    await this.refresh();
    // Don't re-open (re-trap focus / replay animation) for events that
    // originate from within the already-open drawer. Both the drawer itself
    // and its line-items child publish cartUpdate; only external sources
    // (e.g. a quick-add or PDP add-to-cart) should trigger open().
    const internalSources = ["lion-cart-drawer", "lion-cart-drawer-items"];
    if (!internalSources.includes(data?.source)) this.open();
  }

  async refresh() {
    try {
      const res = await fetch(
        `${window.location.pathname}?sections=cart-drawer`,
      );
      if (!res.ok) throw new Error("Section refresh failed");
      const json = await res.json();
      // The Section Rendering API keys results by SECTION ID, not by the
      // custom-element tag. `?sections=cart-drawer` → json["cart-drawer"].
      const html = json["cart-drawer"];
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const fresh = doc.querySelector("lion-cart-drawer");
      if (!fresh) return;
      // Replace only the inner content so event listeners on this element persist
      this.innerHTML = fresh.innerHTML;
      // Re-bind close buttons
      this.querySelectorAll("[data-cart-drawer-close]").forEach((el) =>
        el.addEventListener("click", () => this.close()),
      );
      this.updateHeaderBadge();
    } catch (err) {}
  }

  updateHeaderBadge() {
    fetch(`${window.shopUrls.cart}.js`)
      .then((r) => r.json())
      .then((cart) => {
        const badge = document.querySelector("[data-cart-count]");
        if (!badge) return;
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
        badge.setAttribute("data-pulse", "");
        setTimeout(() => badge.removeAttribute("data-pulse"), 420);
      })
      .catch(() => {});
  }
}
customElements.define("lion-cart-drawer", CartDrawer);

/* ------------------------------------------------------------
 * <lion-cart-drawer-items> — quantity + remove via /cart/change.js
 * ------------------------------------------------------------ */
class CartDrawerItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById(
      "cart-live-region-text",
    );
    this.onChange =
      window.debounce?.(this.onChange.bind(this), 250) ||
      this.onChange.bind(this);
    this.addEventListener("change", this.onChange);
    this.addEventListener("click", (e) => {
      const removeBtn = e.target.closest("[data-cart-remove]");
      if (removeBtn) this.updateQuantity(removeBtn.dataset.index, 0);
    });
  }

  onChange(event) {
    const input = event.target.closest('input[type="number"]');
    if (!input) return;
    this.updateQuantity(input.dataset.index, parseInt(input.value, 10));
  }

  async updateQuantity(line, quantity) {
    const drawer = this.closest("lion-cart-drawer");
    this.classList.add("is-loading");
    try {
      const body = JSON.stringify({
        line: parseInt(line, 10),
        quantity,
        sections: ["cart-drawer"],
        sections_url: window.location.pathname,
      });
      const res = await fetch(`${window.shopUrls.cart_change}`, {
        ...window.fetchConfig(),
        body,
      });
      const state = await res.json();
      if (state.status && state.status !== 200) {
        window.publish?.(window.PUB_SUB_EVENTS.cartError, {
          source: "lion-cart-drawer-items",
          message: state.description,
        });
        return;
      }
      // Patch drawer from returned section HTML
      const html = state.sections?.["cart-drawer"];
      if (html && drawer) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const fresh = doc.querySelector("lion-cart-drawer");
        if (fresh) drawer.innerHTML = fresh.innerHTML;
        drawer
          .querySelectorAll("[data-cart-drawer-close]")
          .forEach((el) => el.addEventListener("click", () => drawer.close()));
        drawer.updateHeaderBadge();
      }
      window.publish?.(window.PUB_SUB_EVENTS.cartUpdate, {
        source: "lion-cart-drawer-items",
      });
    } catch (err) {
    } finally {
      this.classList.remove("is-loading");
    }
  }
}
customElements.define("lion-cart-drawer-items", CartDrawerItems);

/* ------------------------------------------------------------
 * <lion-cart-cross-sell> — fetches complementary recommendations
 * Uses Shopify /recommendations/products endpoint (intent: complementary)
 * ------------------------------------------------------------ */
class CartCrossSell extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.target = this.querySelector("[data-cross-sell-target]");
    if (this.productId && this.target) this.load();
  }

  async load() {
    // Try complementary first — merchant-curated pairs in admin.
    // Fall back to related when nothing is curated so the drawer always has
    // useful suggestions for the visitor.
    const cards =
      (await this.fetchCards("complementary")) ||
      (await this.fetchCards("related"));
    if (!cards || !cards.length) {
      this.setAttribute("hidden", "");
      return;
    }
    this.target.replaceChildren();
    cards.forEach((card) => {
      const li = document.createElement("div");
      li.className = "cart-drawer__cross-sell-item";
      li.appendChild(card.cloneNode(true));
      this.target.appendChild(li);
    });
  }

  async fetchCards(intent) {
    try {
      const url = `/recommendations/products?section_id=cart-recommendations&product_id=${this.productId}&intent=${intent}&limit=${this.limit}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const cards = doc.querySelectorAll(".card-product");
      return cards.length ? cards : null;
    } catch (err) {
      return null;
    }
  }
}
if (!customElements.get("lion-cart-cross-sell"))
  customElements.define("lion-cart-cross-sell", CartCrossSell);

/* ============================================================
 * <lion-cart-delivery-estimate>
 * Computes an honest delivery date window from today + merchant-set
 * business days (min/max), optionally skipping weekends, and renders
 * a localized date using Intl.DateTimeFormat. No fake counters —
 * just a deterministic promise the merchant configured.
 * ============================================================ */
class CartDeliveryEstimate extends HTMLElement {
  constructor() {
    super();
    this.minDays = parseInt(this.dataset.minDays, 10);
    this.maxDays = parseInt(this.dataset.maxDays, 10);
    this.skipWeekends = this.dataset.skipWeekends === "true";
    this.template = this.dataset.template || "Order today, delivery by [date]";
    this.locale = this.dataset.locale || document.documentElement.lang || "en";
  }

  connectedCallback() {
    if (
      isNaN(this.minDays) ||
      isNaN(this.maxDays) ||
      this.minDays < 0 ||
      this.maxDays < this.minDays
    )
      return;
    const range = this.computeRange();
    if (!range) return;
    const dateLabel = this.formatRange(range.start, range.end);
    this.textContent = this.template.replace(/\[date\]/g, dateLabel);
  }

  computeRange() {
    const today = new Date();
    return {
      start: this.addBusinessDays(today, this.minDays),
      end: this.addBusinessDays(today, this.maxDays),
    };
  }

  addBusinessDays(from, days) {
    const result = new Date(from);
    if (days === 0) return result;
    if (!this.skipWeekends) {
      result.setDate(result.getDate() + days);
      return result;
    }
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return result;
  }

  formatRange(start, end) {
    try {
      const fmt = new Intl.DateTimeFormat(this.locale, {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      if (start.toDateString() === end.toDateString()) return fmt.format(end);
      // Range: "Mon 12 — Wed 14 Mar"
      const sameMonth = start.getMonth() === end.getMonth();
      if (sameMonth) {
        const startShort = new Intl.DateTimeFormat(this.locale, {
          weekday: "short",
          day: "numeric",
        }).format(start);
        return `${startShort} – ${fmt.format(end)}`;
      }
      return `${fmt.format(start)} – ${fmt.format(end)}`;
    } catch {
      return end.toLocaleDateString();
    }
  }
}

if (!customElements.get("lion-cart-delivery-estimate")) {
  customElements.define("lion-cart-delivery-estimate", CartDeliveryEstimate);
}
